import { useMemo, useState } from 'react';
import { SCOOP_CATEGORIES, SCOOP_REQUESTS } from '@/mocks/scoopRequests';
import SectionHeading from './SectionHeading';

function urgencyTone(urgency: string) {
  if (urgency === '火') return 'bg-primary-500 text-background-50';
  if (urgency === '急') return 'bg-accent-500 text-background-50';
  return 'bg-secondary-200 text-secondary-900';
}

function categoryTone(category: string) {
  const map: Record<string, string> = {
    文娱瓜田: 'bg-primary-100 text-primary-800 border-primary-200',
    职场真相: 'bg-accent-100 text-accent-900 border-accent-200',
    消费维权: 'bg-primary-100 text-primary-800 border-primary-200',
    平台纠纷: 'bg-secondary-100 text-secondary-900 border-secondary-200',
    学术与公益: 'bg-accent-100 text-accent-900 border-accent-200',
    公共事件: 'bg-secondary-100 text-secondary-900 border-secondary-200',
  };
  return map[category] || 'bg-background-100 text-foreground-700 border-background-200';
}

export default function ScoopBoard() {
  const [active, setActive] = useState('全部征集');

  const counts = useMemo(() => {
    const c: Record<string, number> = { 全部征集: SCOOP_REQUESTS.length };
    SCOOP_REQUESTS.forEach((s) => {
      c[s.category] = (c[s.category] || 0) + 1;
    });
    return c;
  }, []);

  const list = useMemo(
    () => (active === '全部征集' ? SCOOP_REQUESTS : SCOOP_REQUESTS.filter((s) => s.category === active)),
    [active],
  );

  return (
    <section id="board" className="border-b border-background-200 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            index="02"
            kicker="爆料征集令墙"
            title="公开征集令，等你来对号入座"
            sub="每个征集令都对应一个正在寻找的真相方向。按类别筛选，找到你手里那一条，点进去提供线索。"
          />
          <p className="shrink-0 text-[13px] text-foreground-600 md:pb-1">
            当前展示 <span className="font-heading text-lg font-black text-primary-700">{list.length}</span> 条
            <span className="text-foreground-500">（{active}）</span>
          </p>
        </div>

        {/* category filter tabs */}
        <div
          className="mt-8 flex flex-wrap items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1.5"
          role="tablist"
          aria-label="按类别筛选征集令"
        >
          {SCOOP_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={active === cat}
              onClick={() => setActive(cat)}
              className={
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors md:px-4 ' +
                (active === cat
                  ? 'bg-foreground-950 text-foreground-50'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900')
              }
            >
              {cat}
              <span
                className={
                  'rounded-full px-1.5 text-[11px] font-bold ' +
                  (active === cat ? 'bg-primary-500 text-background-50' : 'bg-secondary-200 text-secondary-800')
                }
              >
                {counts[cat] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* feed */}
        <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <i className="ri-inbox-2-line text-4xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-3 font-heading text-lg font-bold text-foreground-800">该分类暂无公开征集令</p>
              <p className="mt-1 text-[14px] text-foreground-500">你也可以直接求料，我们会为它开一条新征集。</p>
              <a
                href="#submit"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                <i className="ri-questionnaire-line" aria-hidden="true"></i>发起求料
              </a>
            </div>
          ) : (
            <ul className="divide-y divide-background-200">
              {list.map((item) => (
                <li key={item.id}>
                  <a
                    href="#submit"
                    className="group grid grid-cols-1 gap-4 px-5 py-5 transition-colors hover:bg-background-100 md:grid-cols-[auto_1fr_auto] md:items-center md:px-7"
                  >
                    <span
                      className="hidden font-heading text-4xl font-black leading-none text-background-300 transition-colors group-hover:text-primary-300 md:flex md:w-12 md:items-center"
                      aria-hidden="true"
                    >
                      {item.num.slice(-2)}
                    </span>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={'rounded-md px-2 py-0.5 text-[11px] font-bold ' + urgencyTone(item.urgency)}>
                          {item.urgency} · {item.urgency === '火' ? '高热度' : item.urgency === '急' ? '紧急寻料' : '公开征集'}
                        </span>
                        <span className={'rounded-full border px-2 py-0.5 text-[11px] font-semibold ' + categoryTone(item.category)}>
                          {item.category}
                        </span>
                        <span className="text-[12px] text-foreground-500">编号 {item.num}</span>
                      </div>
                      <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-800 md:text-lg">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 hidden max-w-2xl text-[13px] leading-relaxed text-foreground-600 sm:block">
                        面向：{item.target}
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-4 md:flex-col md:items-end md:gap-2">
                      <div className="flex flex-col items-start md:items-end">
                        <span className="font-heading text-xl font-black text-primary-700">{item.leads}</span>
                        <span className="text-[11px] text-foreground-500">已有独立线索</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[12px] text-foreground-600">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        <span className="whitespace-nowrap">{item.deadline}</span>
                      </div>
                      <i
                        className="ri-arrow-right-line text-lg text-foreground-300 transition-colors group-hover:text-primary-600"
                        aria-hidden="true"
                      ></i>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-5 flex items-center gap-2 text-[13px] text-foreground-500">
          <i className="ri-lock-line text-primary-600" aria-hidden="true"></i>
          所有提供者均可匿名，线索采用“只认线索、不认人”的处理方式。
        </p>
      </div>
    </section>
  );
}